import { useMemo, type ReactNode } from "react";
import intl from "react-intl-universal";
import { ListItemIcon, ListItemText, Box } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { IMenuRoute, MenuSectionId, routes } from "../../../Constants/Routes";
import {
  Container,
  MenuItem,
  Menu,
  MenuSectionDivider,
  MenuSectionLabel,
} from "./styles";
import { useAuth, PermissionId } from "../../../Contexts/AuthContext";
import { useColorMode } from "../../../Hooks/useColorMode";
import { useBaasInstance } from "../../../Contexts/BaasInstanceContext";
import { BaasSidebar } from "../../../Pages/Private/BaasConsole/components/BaasSidebar";
import { BaasInstancesSidebar } from "../../../Pages/Private/BaasConsole/components/BaasInstancesSidebar";
import {
  isBaasPath,
  shouldUseBaasInstancesSidebar,
} from "../../../Pages/Private/BaasConsole/constants";
import { useIbProductFeaturesStore } from "../../../Stores/ibProductFeaturesStore";
import { isIbProductEnabled } from "../../../Utils/ibProductFeatures";
import type { IbProductFeatureCode } from "../../../Constants/Routes";
import { useActiveIbAccount } from "../../../Hooks/useActiveIbAccount";

const MENU_SECTION_ORDER: MenuSectionId[] = [
  "conta",
  "gestao",
  "produtos",
];

/** Sections that render a visible category label above the first item. */
const SECTION_LABEL_KEYS: Partial<Record<MenuSectionId, string>> = {
  conta: "nav.section.conta",
  produtos: "nav.section.produtos",
};

const badgeSx = {
  height: 16,
  minWidth: 16,
  px: 0.75,
  py: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "9999px",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.02em",
  lineHeight: 1,
} as const;

const isMenuItemActive = (pathname: string, route: IMenuRoute) => {
  const routePath = route.path;

  if (route.exact === true || routePath === "/") {
    return pathname === routePath;
  }

  return pathname === routePath || pathname.startsWith(`${routePath}/`);
};

const getVisibleMenuRoutes = (
  hasPermission: (permission: PermissionId) => boolean,
  canAccessCarteira: boolean,
  canAccessConsole: boolean,
  isFeatureEnabled: (code?: IbProductFeatureCode) => boolean,
  isExitRailVirtual: boolean,
  isReadOnly: boolean,
) =>
  routes
    .filter((route) => !!route.icon)
    .sort((a, b) => Number(!!a.comingSoon) - Number(!!b.comingSoon))
    .filter((route) => {
      if (route.isHidden) return false;
      if (!canAccessCarteira) {
        // Console-only: only BaaS console entry remains in the IB sidebar.
        return route.path === "/baas" && canAccessConsole;
      }
      if (route.requiresConsoleAccess && !canAccessConsole) return false;
      if (route.hideWhenExitRailVirtual && isExitRailVirtual) return false;
      if (route.hideWhenReadOnly && isReadOnly) return false;
      if (route.permission && !hasPermission(route.permission)) return false;
      if (route.requiredFeature && !isFeatureEnabled(route.requiredFeature)) return false;
      return true;
    });

const sortRoutesBySection = (visibleRoutes: IMenuRoute[]) => {
  const sectionOrder = new Map(MENU_SECTION_ORDER.map((id, index) => [id, index]));

  return [...visibleRoutes].sort((a, b) => {
    const aSection = a.menuSection ? (sectionOrder.get(a.menuSection) ?? 999) : 999;
    const bSection = b.menuSection ? (sectionOrder.get(b.menuSection) ?? 999) : 999;

    if (aSection !== bSection) return aSection - bSection;

    return routes.indexOf(a) - routes.indexOf(b);
  });
};

interface MenuNavigationProps {
  onNavigate?: () => void;
}

export const MenuNavigation = ({ onNavigate }: MenuNavigationProps) => {
  const location = useLocation();
  const { hasPermission, user, isReadOnly } = useAuth();
  const { isDark } = useColorMode();

  const { selectedInstanceId } = useBaasInstance();
  const { isExitRailVirtual } = useActiveIbAccount();
  const features = useIbProductFeaturesStore((state) => state.features);
  const isInBaasInstances = shouldUseBaasInstancesSidebar(
    location.pathname,
    selectedInstanceId,
  );
  const isInBaasConsole = isBaasPath(location.pathname) && !isInBaasInstances;
  const canAccessCarteira = user.canAccessCarteira !== false;
  const canAccessConsole = user.canAccessConsole === true;

  const isFeatureEnabled = useMemo(
    () => (code?: IbProductFeatureCode) => isIbProductEnabled(features, code),
    [features],
  );

  const visibleRoutes = useMemo(
    () =>
      sortRoutesBySection(
        getVisibleMenuRoutes(
          hasPermission,
          canAccessCarteira,
          canAccessConsole,
          isFeatureEnabled,
          isExitRailVirtual,
          isReadOnly,
        ),
      ),
    [
      hasPermission,
      canAccessCarteira,
      canAccessConsole,
      isFeatureEnabled,
      isExitRailVirtual,
      isReadOnly,
    ],
  );

  if (isInBaasInstances) {
    return <BaasInstancesSidebar onNavigate={onNavigate} />;
  }

  if (isInBaasConsole) {
    return <BaasSidebar onNavigate={onNavigate} />;
  }

  const renderMenuItem = (el: IMenuRoute) => {
    const Icon = el.icon;
    const isComingSoon = !!el.comingSoon;
    const isSelected = !isComingSoon && isMenuItemActive(location.pathname, el);
    const menuLabel = el.labelKey ? intl.get(el.labelKey) : (el.label ?? "");
    const badgeLabel = el.badgeKey ? intl.get(el.badgeKey) : el.badge;

    const menuItem = (
      <MenuItem
        $isSelected={isSelected}
        $isDisabled={isComingSoon}
        selected={false}
        disableRipple
      >
        <ListItemIcon sx={{ minWidth: 28, mr: 0.5, color: "inherit" }}>
          <Icon size={18} />
        </ListItemIcon>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", minWidth: 0 }}>
          <ListItemText
            primary={menuLabel}
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: isSelected ? 600 : 500,
              lineHeight: 1.25,
              noWrap: true,
            }}
          />
          {isComingSoon && (
            <Box
              component="span"
              sx={{
                ...badgeSx,
                ml: "auto",
                flexShrink: 0,
                bgcolor: isDark ? "#262626" : "#f3f4f6",
                color: isDark ? "#9ca3af" : "#6b7280",
              }}
            >
              {intl.get("status.comingSoon")}
            </Box>
          )}
          {!isComingSoon && badgeLabel && (
            <Box
              component="span"
              sx={{
                ...badgeSx,
                ml: "auto",
                flexShrink: 0,
                background: "var(--brand-badge-bg, linear-gradient(90deg, rgba(255, 218, 155, 0.35), rgba(235, 186, 102, 0.22)))",
                border: "1px solid var(--brand-badge-border, rgba(235, 186, 102, 0.55))",
                color: "var(--brand-accent-muted, #9a7340)",
              }}
            >
              {badgeLabel}
            </Box>
          )}
        </Box>
      </MenuItem>
    );

    if (isComingSoon) {
      return <li key={el.path}>{menuItem}</li>;
    }

    return (
      <Link key={el.path} to={el.path} className="link-no-zoom" onClick={onNavigate}>
        {menuItem}
      </Link>
    );
  };

  const menuNodes: ReactNode[] = [];
  let previousSection: MenuSectionId | undefined;

  visibleRoutes.forEach((el) => {
    const section = el.menuSection;
    const isNewSection =
      section !== undefined && section !== previousSection;

    if (isNewSection && section) {
      // Dividers between Conta → gestão and gestão → Produtos.
      if (
        previousSection !== undefined &&
        (section === "gestao" || section === "produtos")
      ) {
        menuNodes.push(
          <MenuSectionDivider key={`section-divider-${section}`} role="separator" />,
        );
      }

      const labelKey = SECTION_LABEL_KEYS[section];
      if (labelKey) {
        menuNodes.push(
          <MenuSectionLabel key={`section-label-${section}`}>
            {intl.get(labelKey)}
          </MenuSectionLabel>,
        );
      }
    }

    if (section !== undefined) {
      previousSection = section;
    }

    menuNodes.push(renderMenuItem(el));
  });

  return (
    <Container aria-label={intl.get("nav.sidebarLabel")}>
      <Menu>{menuNodes}</Menu>
    </Container>
  );
};
